import { Loader2, Inbox } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { money } from "@/lib/format";

const STATUS = {
  draft: "bg-slate-100 text-slate-600 border-slate-300",
  validated: "bg-blue-50 text-blue-700 border-blue-200",
  signed: "bg-purple-50 text-purple-800 border-purple-200",
  sent: "bg-sky-50 text-sky-700 border-sky-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-zinc-100 text-zinc-500 border-zinc-300 line-through",
  converted: "bg-amber-50 text-amber-800 border-amber-200",
};

export const StatusBadge = ({ status, testid }) => {
  const { t } = useApp();
  return (
    <span data-testid={testid || `status-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${STATUS[status] || STATUS.draft}`}>
      {status === "sent" && <Loader2 className="h-3 w-3 animate-spin" />}
      {t(`st_${status}`)}
    </span>
  );
};

export const Money = ({ v, cur, className = "" }) => {
  const { lang } = useApp();
  return <span className={`num whitespace-nowrap ${className}`} dir="ltr">{money(v, cur, lang)}</span>;
};

export const PageHeader = ({ eyebrow, title, children }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between fade-up">
    <div>
      {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
    </div>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

export const Panel = ({ title, action, children, className = "", testid }) => (
  <section data-testid={testid} className={`rounded-xl border border-slate-200/80 bg-white shadow-sm ${className}`}>
    {title && (
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 sm:px-5">
        <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
        {action}
      </div>
    )}
    <div className="p-4 sm:p-5">{children}</div>
  </section>
);

export const Kpi = ({ label, value, sub, accent = "teal", testid }) => (
  <div data-testid={testid} className="relative overflow-hidden rounded-xl border border-slate-200/80 bg-white p-5 shadow-sm fade-up">
    <div className={`absolute inset-y-0 start-0 w-1 ${accent === "orange" ? "bg-orange-700" : accent === "navy" ? "bg-slate-900" : accent === "red" ? "bg-red-600" : "bg-teal-600"}`} />
    <div className="eyebrow">{label}</div>
    <div className="mt-2 text-xl sm:text-2xl font-semibold text-slate-900">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

export const Empty = ({ text }) => {
  const { t } = useApp();
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-400" data-testid="empty-state">
      <Inbox className="h-8 w-8" />
      <p className="text-sm">{text || t("empty")}</p>
    </div>
  );
};

export const Loading = () => (
  <div className="flex items-center justify-center py-16 text-slate-400" data-testid="loading"><Loader2 className="h-6 w-6 animate-spin" /></div>
);

export const Field = ({ label, children, className = "" }) => (
  <label className={`flex flex-col gap-1.5 ${className}`}>
    <span className="text-xs font-medium text-slate-600">{label}</span>
    {children}
  </label>
);

export const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2.5 text-start text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap ${className}`}>{children}</th>
);
export const Td = ({ children, className = "", ...p }) => <td className={`px-3 py-2.5 text-sm text-slate-700 align-middle ${className}`} {...p}>{children}</td>;

export const TableWrap = ({ children }) => (
  <div className="overflow-x-auto -mx-4 sm:-mx-5"><table className="w-full min-w-[640px] border-collapse">{children}</table></div>
);

export const NativeSelect = ({ className = "", ...p }) => (
  <select className={`h-9 rounded-md border border-input bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/30 ${className}`} {...p} />
);
